/**
 * src/lib/services/notifications.ts
 * Notification preference service — GET and PUT user preferences.
 * Also the real-time (SSE) stream URL and web push subscription endpoints
 * — see claude/feature-scope-realtime-push-notifications.md in the
 * project for the full feature scope.
 */
import { API_BASE_URL, del, get, post, postKeepalive, put } from '@/lib/api/client';

export type NotificationPreferences = {
  user_id: string;
  new_booking_received: boolean;
  substitution_required: boolean;
  daily_digest_email: boolean;
  audit_log_alerts: boolean;
};

// daily_digest_email is deliberately absent: alerts are in-app only now, and
// the "While you were away" panel (see getMissedNotifications) replaces the
// old emailed digest. The backend defaults the field when it is omitted.
export type NotificationPreferencesUpdate = {
  new_booking_received: boolean;
  substitution_required: boolean;
  audit_log_alerts: boolean;
};

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  return get<NotificationPreferences>('/api/v1/notifications/preferences');
}

export async function updateNotificationPreferences(
  prefs: NotificationPreferencesUpdate,
): Promise<NotificationPreferences> {
  return put<NotificationPreferences>('/api/v1/notifications/preferences', prefs);
}

// ── Missed alerts ("While you were away") ───────────────────────────────
// Every in-app event is persisted server-side. On login the app asks for
// everything the operator hasn't seen yet (already filtered by their toggles)
// and, once they dismiss the panel, advances their per-user watermark.

export type MissedEvent = {
  id: string;
  event_type: string;
  workflow_id: string | null;
  title: string;
  body: string;
  created_at: string;
};

export type MissedDailyStats = {
  date: string;
  sent: number;
  rejected: number;
  pending_approval: number;
};

export type MissedNotificationsResponse = {
  since: string | null;
  // Newest event timestamp BEFORE toggle filtering — echo it to markNotificationsSeen.
  until: string | null;
  events: MissedEvent[];
  counts: Record<string, number>;
  daily_stats: MissedDailyStats | null;
};

export async function getMissedNotifications(): Promise<MissedNotificationsResponse> {
  return get<MissedNotificationsResponse>('/api/v1/notifications/missed');
}

export async function markNotificationsSeen(through: string): Promise<void> {
  await post('/api/v1/notifications/seen', { through });
}

/** Best-effort watermark advance while the page is unloading. */
export function markNotificationsSeenOnUnload(through: string): void {
  postKeepalive('/api/v1/notifications/seen', { through });
}

// ── Real-time (SSE) + web push ──────────────────────────────────────────
// Proxied through api-gateway's dedicated streaming route (see
// glow-ai-api-gateway/app/main.py's notifications_stream_proxy), which
// forwards to notification-service. Cookie-authed like every other
// endpoint — EventSource just needs withCredentials: true since it can't
// go through the fetch-based request() wrapper.

// Built from the same API_BASE_URL fetch() uses, so this stays correct
// whether the gateway is reached via an absolute domain (staging) or a
// relative path (local dev proxy).
export const NOTIFICATIONS_STREAM_URL = `${API_BASE_URL}/api/v1/notifications/stream`;

export type VapidPublicKeyResponse = {
  vapid_public_key: string;
};

export async function getVapidPublicKey(): Promise<string> {
  const data = await get<VapidPublicKeyResponse>('/api/v1/notifications/vapid-public-key');
  return data.vapid_public_key;
}

export type PushSubscriptionPayload = {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
};

export async function subscribeToPush(payload: PushSubscriptionPayload): Promise<void> {
  await post('/api/v1/notifications/push-subscribe', payload);
}

export async function unsubscribeFromPush(endpoint: string): Promise<void> {
  await del('/api/v1/notifications/push-unsubscribe', { endpoint });
}
